<template>
  <div class="status-card-wrapper">
    <div class="status-card" :class="cardClass">
      <!-- Icon -->
      <div class="status-icon" :class="iconClass">
        <slot name="icon">
          <!-- Success Icon -->
          <svg v-if="variant === 'success'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
          <!-- Error Icon -->
          <svg v-else-if="variant === 'error'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="15" y1="9" x2="9" y2="15"></line>
            <line x1="9" y1="9" x2="15" y2="15"></line>
          </svg>
          <!-- Warning Icon -->
          <svg v-else-if="variant === 'warning'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
          <!-- Info Icon -->
          <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
          </svg>
        </slot>
      </div>

      <!-- Title -->
      <h2 v-if="title" class="status-title" :class="titleClass">{{ title }}</h2>

      <!-- Message -->
      <p v-if="message" class="status-message">{{ message }}</p>

      <!-- Submessage -->
      <p v-if="submessage" class="status-submessage">{{ submessage }}</p>

      <!-- Custom Content Slot -->
      <div v-if="$slots.default" class="status-content">
        <slot></slot>
      </div>

      <!-- Actions Slot -->
      <div v-if="$slots.actions" class="status-actions">
        <slot name="actions"></slot>
      </div>

      <!-- Help Link -->
      <div v-if="showHelp" class="status-help">
        <span class="help-label">Need help?</span>
        <a :href="helpUrl" target="_blank" rel="noopener" class="help-link">
          Contact Support
        </a>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'StatusCard',

  props: {
    variant: {
      type: String,
      default: 'info',
      validator: (value) => ['success', 'error', 'warning', 'info'].includes(value)
    },
    title: {
      type: String,
      default: ''
    },
    message: {
      type: String,
      default: ''
    },
    submessage: {
      type: String,
      default: ''
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['sm', 'md', 'lg'].includes(value)
    },
    showHelp: {
      type: Boolean,
      default: false
    },
    helpUrl: {
      type: String,
      default: 'https://chrilantech.com'
    }
  },

  computed: {
    cardClass() {
      return [
        `status-card--${this.variant}`,
        `status-card--${this.size}`
      ]
    },
    iconClass() {
      return `status-icon--${this.variant}`
    },
    titleClass() {
      return `status-title--${this.variant}`
    }
  }
}
</script>

<style scoped>
.status-card-wrapper {
  display: flex;
  justify-content: center;
  padding: var(--space-8, 2rem) 0;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.status-card {
  background: var(--bg-card, #ffffff);
  border: 1px solid var(--border-color, #e4e4e7);
  border-radius: var(--card-radius, 0.75rem);
  padding: var(--space-10, 2.5rem);
  text-align: center;
  width: 100%;
}

/* Size Variants */
.status-card--sm {
  max-width: 320px;
  padding: var(--space-6, 1.5rem);
}

.status-card--md {
  max-width: 400px;
}

.status-card--lg {
  max-width: 480px;
}

/* Icon */
.status-icon {
  width: 64px;
  height: 64px;
  border-radius: var(--radius-full, 50%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto var(--space-5, 1.25rem);
  animation: scaleIn 0.3s ease-out;
}

@keyframes scaleIn {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.status-card--lg .status-icon {
  width: 80px;
  height: 80px;
  margin-bottom: var(--space-6, 1.5rem);
}

.status-card--sm .status-icon {
  width: 48px;
  height: 48px;
  margin-bottom: var(--space-4, 1rem);
}

.status-icon svg {
  width: 32px;
  height: 32px;
}

.status-card--lg .status-icon svg {
  width: 40px;
  height: 40px;
}

.status-card--sm .status-icon svg {
  width: 24px;
  height: 24px;
}

/* Icon Variants */
.status-icon--success {
  background: var(--color-success-50, #ecfdf5);
  color: var(--color-success-500, #10b981);
}

.status-icon--error {
  background: var(--color-error-100, #fee2e2);
  color: var(--color-error-600, #dc2626);
}

.status-icon--warning {
  background: var(--color-warning-100, #fef3c7);
  color: var(--color-warning-600, #d97706);
}

.status-icon--info {
  background: var(--color-primary-50, #e0f2fe);
  color: var(--color-primary-500, #0ea5e9);
}

/* Title */
.status-title {
  font-size: var(--text-2xl, 1.125rem);
  font-weight: var(--font-semibold, 600);
  margin: 0 0 var(--space-2, 0.5rem) 0;
}

.status-card--lg .status-title {
  font-size: var(--text-4xl, 1.5rem);
  margin-bottom: var(--space-3, 0.75rem);
}

.status-card--sm .status-title {
  font-size: var(--text-xl, 1rem);
}

/* Title Variants */
.status-title--success {
  color: var(--color-success-500, #10b981);
}

.status-title--error {
  color: var(--color-error-600, #dc2626);
}

.status-title--warning {
  color: var(--color-warning-600, #d97706);
}

.status-title--info {
  color: var(--text-primary, #18181b);
}

/* Message */
.status-message {
  font-size: var(--text-md, 0.875rem);
  color: var(--text-secondary, #52525b);
  line-height: var(--leading-relaxed, 1.6);
  margin: 0;
}

.status-card--lg .status-message {
  font-size: var(--text-lg, 0.9375rem);
}

/* Submessage */
.status-submessage {
  font-size: var(--text-md, 0.875rem);
  color: var(--text-muted, #71717a);
  margin: var(--space-1, 0.25rem) 0 0 0;
}

/* Content */
.status-content {
  margin-top: var(--space-6, 1.5rem);
}

/* Actions */
.status-actions {
  margin-top: var(--space-6, 1.5rem);
  display: flex;
  justify-content: center;
  gap: var(--space-3, 0.75rem);
}

/* Help */
.status-help {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2, 0.5rem);
  font-size: var(--text-base, 0.8125rem);
  padding-top: var(--space-4, 1rem);
  margin-top: var(--space-6, 1.5rem);
  border-top: 1px solid var(--border-color, #e4e4e7);
}

.help-label {
  color: var(--text-muted, #71717a);
}

.help-link {
  color: var(--color-primary-500, #0ea5e9);
  text-decoration: none;
  font-weight: var(--font-medium, 500);
}

.help-link:hover {
  text-decoration: underline;
}

/* Responsive */
@media (max-width: 576px) {
  .status-card-wrapper {
    padding: var(--space-4, 1rem) 0;
  }

  .status-card {
    padding: var(--space-6, 1.5rem);
  }

  .status-icon {
    width: 56px;
    height: 56px;
  }

  .status-icon svg {
    width: 28px;
    height: 28px;
  }
}
</style>
